import { type EventChannel, eventChannel } from 'redux-saga'

// A short hop takes the minimum; a jump across the whole board takes the
// maximum, so a far target is not a blink. Linear in between.
export const JUMP_MIN_DURATION_MS = 300
export const JUMP_MAX_DURATION_MS = 700
export const JUMP_FAR_DISTANCE_PX = 2000

// Closer than this the animation would be a single frame anyway — set the
// translate at once.
export const JUMP_MIN_DISTANCE_PX = 2

interface Size {
  width: number
  height: number
}

export const easeOutCubic = (progress: number) => 1 - (1 - progress) ** 3

// Translate that puts the element in the middle of the viewport, given where
// it is on screen now and the current translate of the movable container.
export const getCenteredTranslate = (
  rect: DOMRect,
  translate: number[],
  viewport: Size
): number[] => {
  const x = -rect.left + translate[0] + viewport.width / 2 - rect.width / 2
  const y = -rect.top + translate[1] + viewport.height / 2 - rect.height / 2

  return [x, y]
}

export const distance = (from: number[], to: number[]) =>
  Math.hypot(to[0] - from[0], to[1] - from[1])

export const getJumpDuration = (distancePx: number) => {
  const far = Math.min(distancePx / JUMP_FAR_DISTANCE_PX, 1)

  return (
    JUMP_MIN_DURATION_MS + (JUMP_MAX_DURATION_MS - JUMP_MIN_DURATION_MS) * far
  )
}

// Progress 1 returns `to` itself so the last frame lands exactly on target,
// without float noise from the interpolation.
export const interpolateTranslate = (
  from: number[],
  to: number[],
  progress: number
): number[] => {
  if (progress >= 1) {
    return to
  }

  const eased = easeOutCubic(progress)

  return [
    from[0] + (to[0] - from[0]) * eased,
    from[1] + (to[1] - from[1]) * eased,
  ]
}

export const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

// Emits the requestAnimationFrame timestamp every frame until closed. The next
// frame is requested before the current one is emitted: the saga usually
// finishes inside emit, and closing then has to cancel the frame that is
// really pending, not the one that just fired.
export const createFrameChannel = (): EventChannel<number> =>
  eventChannel<number>((emit) => {
    let closed = false
    let frameId = requestAnimationFrame(function tick(time) {
      if (closed) {
        return
      }

      frameId = requestAnimationFrame(tick)
      emit(time)
    })

    return () => {
      closed = true
      cancelAnimationFrame(frameId)
    }
  })
